import React, { useState, useEffect } from "react";
import { attendanceAPI, userAPI } from "../../lib/api";
import { toast } from "sonner";
import { formatDate, formatTime } from "../../lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Calendar } from "../../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../components/ui/popover";
import { CalendarIcon, Download, Users } from "lucide-react";
import { format } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const AttendanceReportPage = () => {
  const [attendance, setAttendance] = useState([]);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState("all");
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().setDate(1)),
    to: new Date(),
  });

  useEffect(() => {
    fetchData();
  }, [dateRange, selectedUser]);

  const fetchData = async () => {
    try {
      const params = {
        start_date: format(dateRange.from, "yyyy-MM-dd"),
        end_date: format(dateRange.to, "yyyy-MM-dd"),
      };
      if (selectedUser !== "all") {
        params.user_id = selectedUser;
      }
      const [attendanceRes, usersRes] = await Promise.all([
        attendanceAPI.getAll(params),
        userAPI.getAll(),
      ]);
      setAttendance(attendanceRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      toast.error("Failed to fetch attendance data");
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    const grouped = {};
    attendance.forEach((record) => {
      const date = record.date;
      if (!grouped[date]) {
        grouped[date] = { date, present: 0, absent: 0 };
      }
      if (record.punch_in) {
        grouped[date].present++;
      } else {
        grouped[date].absent++;
      }
    });
    return Object.values(grouped).slice(-7);
  };

  const stats = {
    total: attendance.length,
    present: attendance.filter((a) => a.punch_in).length,
    absent: attendance.filter((a) => !a.punch_in).length,
    avgHours: attendance.reduce((acc, a) => acc + (a.working_hours || 0), 0) / attendance.filter((a) => a.working_hours).length || 0,
  };

  const handleExportCSV = () => {
    const headers = ["Employee ID", "Employee Name", "Date", "Punch In", "Punch Out", "Working Hours", "Status"];
    const rows = attendance.map(record => {
      const emp = users.find(u => u.id === record.user_id);
      return [
        emp?.employee_id || "",
        emp ? `${emp.first_name} ${emp.last_name}` : "-",
        record.date,
        record.punch_in || "-",
        record.punch_out || "-",
        record.working_hours?.toFixed(2) || "0",
        record.punch_in ? "Present" : "Absent"
      ];
    });
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `attendance_report_${format(dateRange.from, "yyyy-MM-dd")}_to_${format(dateRange.to, "yyyy-MM-dd")}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success("Report exported successfully");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="attendance-report-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Attendance Reports</h1>
          <p className="text-slate-500">View and analyze attendance data</p>
        </div>
        <Button variant="outline" onClick={handleExportCSV} data-testid="export-attendance-btn">
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-400" />
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <SelectTrigger className="w-48" data-testid="user-filter-select">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.first_name} {user.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {format(dateRange.from, "MMM d")} - {format(dateRange.to, "MMM d, yyyy")}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="range"
                  selected={dateRange}
                  onSelect={(range) => range && setDateRange(range)}
                  numberOfMonths={2}
                />
              </PopoverContent>
            </Popover>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="stat-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Total Records</p>
            <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Present</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.present}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Absent</p>
            <p className="text-2xl font-bold text-red-600">{stats.absent}</p>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-5">
            <p className="text-sm text-slate-500">Avg. Hours</p>
            <p className="text-2xl font-bold text-slate-900">{stats.avgHours.toFixed(1)}</p>
          </CardContent>
        </Card>
      </div>

      {/* Chart */}
      <Card className="chart-container">
        <CardHeader>
          <CardTitle className="font-heading">Attendance Trend</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={getChartData()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" stroke="#64748B" tickFormatter={(v) => format(new Date(v), "MMM d")} />
                <YAxis stroke="#64748B" />
                <Tooltip
                  contentStyle={{ backgroundColor: "white", border: "1px solid #E2E8F0", borderRadius: "8px" }}
                />
                <Legend />
                <Bar dataKey="present" fill="#10B981" name="Present" radius={[4, 4, 0, 0]} />
                <Bar dataKey="absent" fill="#EF4444" name="Absent" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Attendance Table */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Detailed Records</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Punch In</TableHead>
                <TableHead>Punch Out</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {attendance.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    No records found
                  </TableCell>
                </TableRow>
              ) : (
                attendance.map((record) => {
                  const user = users.find((u) => u.id === record.user_id);
                  return (
                    <TableRow key={record.id}>
                      <TableCell className="font-medium">
                        {user ? `${user.first_name} ${user.last_name}` : "-"}
                      </TableCell>
                      <TableCell>{formatDate(record.date)}</TableCell>
                      <TableCell>{formatTime(record.punch_in)}</TableCell>
                      <TableCell>{record.punch_out ? formatTime(record.punch_out) : "-"}</TableCell>
                      <TableCell>{record.working_hours?.toFixed(2) || "-"}</TableCell>
                      <TableCell>
                        <Badge variant={record.punch_in ? "default" : "destructive"}>
                          {record.punch_in ? "Present" : "Absent"}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default AttendanceReportPage;
